import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { kebabCase } from 'change-case';
import { Navigate, Route, Routes as RouterRoutes } from 'react-router-dom';

import { AstroSmartLinkDomain } from '@/generated/gql/graphql';
import { BaseDomainType, NegotiationDomainType } from '~/common/types';
import { AppContent } from '~/components/AppContent/AppContent';
import { useConfig } from '~/components/ConfigProvider';
import { RequireAuth } from '~/components/RequireAuth/RequireAuth';
import { RestrictedRoute } from '~/components/RestrictedRoute/RestrictedRoute';
import { Analytics } from '~/routes/Analytics/Analytics';
import { CommodityDetail } from '~/routes/Analytics/Base/CommodityDetail/CommodityDetail';
import { CommodityGroupDetail } from '~/routes/Analytics/Base/CommodityGroupDetail/CommodityGroupDetail';
import { CommodityGroupList } from '~/routes/Analytics/Base/CommodityGroupList/CommodityGroupList';
import { CommodityList } from '~/routes/Analytics/Base/CommodityList/CommodityList';
import { CompanyDetail as AnalyticsCompanyDetail } from '~/routes/Analytics/Base/CompanyDetail/CompanyDetail';
import { ContractDetail } from '~/routes/Analytics/Base/ContractDetail/ContractDetail';
import { ContractList } from '~/routes/Analytics/Base/ContractList/ContractList';
import { BaseGeneral } from '~/routes/Analytics/Base/General/BaseGeneral';
import { PurchaseOrderDetail as AnalyticsPurchaseOrderDetail } from '~/routes/Analytics/Base/PurchaseOrderDetail/PurchaseOrderDetail';
import { PurchaseOrderList } from '~/routes/Analytics/Base/PurchaseOrderList/PurchaseOrderList';
import { PurchaseRequisitionDetail } from '~/routes/Analytics/Base/PurchaseRequisitionDetail/PurchaseRequisitionDetail';
import { BasePurchaseRequisitionList } from '~/routes/Analytics/Base/PurchaseRequisitionList/PurchaseRequisitionList';
import { SupplierDetail } from '~/routes/Analytics/Base/SupplierDetail/SupplierDetail';
import { BaseSupplierList, SupplierListTab } from '~/routes/Analytics/Base/SupplierList/SupplierList';
import { DataFoundation } from '~/routes/DataFoundation/DataFoundation';
import { Savings } from '~/routes/DataFoundation/Savings/Savings';
import { ProcurementKnowledgeList } from '~/routes/ProcurementKnowledge/ProcurementKnowledgeList';
import { RuleEvaluationLog } from '~/routes/RuleEvaluationLog/RuleEvaluationLog';
import { RuleEvaluationLogDetail } from '~/routes/RuleEvaluationLogDetail/RuleEvaluationLogDetail';
import { RuleListSourcing } from '~/routes/RuleListSourcing/RuleListSourcing.tsx';
import { useSearchDomains } from '~/routes/Search/hooks/useSearchDomains/useSearchDomains.ts';

import { SourcingProjectDetail } from './Analytics/Base/SourcingProjectDetail/SourcingProjectDetail';
import { Superset } from './Analytics/Superset/Superset';
import { AuthCallback } from './auth/OAuthCallback';
import { PopupCallback } from './auth/PopupCallback';
import { TeamsAuthCallback } from './auth/TeamsAuthCallback';
import { TeamsLogin } from './auth/TeamsLogin';
import { ContractDemandDetail } from './autonomousSourcing/ContractDemandDetail/ContractDemandDetail';
import { ContractDemandList } from './autonomousSourcing/ContractDemandList/ContractDemandList';
import { CoverageAssistant } from './autonomousSourcing/CoverageAssistant/CoverageAssistant';
import { SourcingCaseDetail } from './autonomousSourcing/SourcingCaseDetail/SourcingCaseDetail';
import { Cmdi } from './Cmdi/Cmdi';
import { DemoLayout } from './Demo/DemoLayout';
import { Cockpit } from './experiments/Cockpit/Cockpit';
import { CockpitCreate } from './experiments/Cockpit/components/CockpitCreate/CockpitCreate';
import { DemandConversationPage } from './experiments/Cockpit/components/DemandConversationPage/DemandConversationPage';
import { Thread } from './experiments/Cockpit/components/Thread/Thread';
import { CountryReportDetail } from './experiments/RiskSustainability/CountryReportDetail/CountryReportDetail';
import { RiskCockpit } from './experiments/RiskSustainability/RiskCockpit/RiskCockpit';
import { RiskDetail } from './experiments/RiskSustainability/RiskDetail/RiskDetail';
import { Home } from './Home/Home';
import { IntakeChat } from './IntakeChat/IntakeChat';
import { IntakeCreate } from './IntakeCreate/IntakeCreate';
import { IntakeCreateChat } from './IntakeCreateChat/IntakeCreateChat';
import { IntakeDetail } from './IntakeDetail/IntakeDetail';
import { IntakeDetailPage } from './IntakeDetail/IntakeDetailPage';
import { IntakeList } from './IntakeList/IntakeList';
import { InternalTools } from './InternalTools/InternalTools';
import { LandingPage } from './LandingPage/LandingPage';
import { Login } from './Login/Login';
import { NegotiationCaseCreate } from './NegotiationCaseCreate/NegotiationCaseCreate';
import { NegotiationCaseDetail } from './NegotiationCaseDetail/NegotiationCaseDetail';
import { NegotiationCaseList } from './NegotiationCaseList/NegotiationCaseList';
import { NegotiationDemandDetail } from './NegotiationDemandDetail/NegotiationDemandDetail';
import { NegotiationDemandList } from './NegotiationDemandList/NegotiationDemandList';
import { NotFound } from './NotFound/NotFound';
import { RouteId, RouteIdPart } from './RouteId';
import { RuleDetailCreate } from './RuleDetail/components/RuleDetailCreate/RuleDetailCreate';
import { RuleDetail } from './RuleDetail/RuleDetail';
import { Search } from './Search/Search';
import { SmartLinks } from './SmartLinks/SmartLinks';
import { Support } from './Support/Support';
import { TeamManagement } from './TeamManagement/TeamManagement';
import { UserAvailabilityList } from './UserAvailabilityList/UserAvailabilityList';
import { UserProfile } from './UserProfile/UserProfile';

export const Routes = () => {
  const { hasPermission } = usePermissions();
  const { features } = useConfig();
  const { searchDomains } = useSearchDomains();

  return (
    <>
      <RouterRoutes>
        {/* START UNPROTECTED ROUTES */}
        <Route path={toRelativePath(RouteId.popupCallback)} element={<PopupCallback />} />
        <Route path={toRelativePath(RouteId.login)} element={<Login />} />
        <Route path={toRelativePath(RouteId.loginTeamsStart)} element={<TeamsLogin />} />
        <Route path={toRelativePath(RouteId.loginTeamsEnd)} element={<TeamsAuthCallback />} />
        <Route path={toRelativePath(RouteId.authCallback)} element={<AuthCallback />} />
        {/* END UNPROTECTED ROUTES */}

        {/* START PROTECTED ROUTES */}
        <Route
          path="*"
          element={
            <RequireAuth>
              <AppContent />
            </RequireAuth>
          }
        >
          {/* Backwards compatibility: Reroute outdated /base urls  */}
          <Route path="base" element={<Navigate to={RouteId.analyticsBase('')} replace />} />
          {hasPermission(Permission.SearchView) && (
            <Route path={toRelativePath(RouteId.search)}>
              <Route index element={<Search />} />
              {searchDomains.map((searchDomain) => (
                <Route key={searchDomain} path={kebabCase(searchDomain)} element={<Search domain={searchDomain} />} />
              ))}
            </Route>
          )}
          <Route path={toRelativePath(RouteId.root)} element={<LandingPage />} />
          <Route path={toRelativePath(RouteId.userProfile(''))}>
            <Route index element={<UserProfile />} />
            <Route path=":userId">
              <Route index element={<UserProfile />} />
            </Route>
          </Route>

          {hasPermission(Permission.UserAvailabilityListView) && (
            <Route path={toRelativePath(RouteId.team)} element={<UserAvailabilityList />} />
          )}
          {hasPermission(Permission.RouteCockpit) && (
            <Route path={toRelativePath(RouteId.cockpit)} element={<Cockpit />}>
              <Route index element={<CockpitCreate />} />
              <Route path={RouteIdPart.conversation}>
                <Route path=":id" element={<Thread />} />
                <Route path={`${RouteIdPart.demand}/:id`} element={<DemandConversationPage />} />
              </Route>
              <Route path={RouteIdPart.guided}>
                <Route path=":id" element={<IntakeDetail />} />
              </Route>
            </Route>
          )}

          {hasPermission(Permission.RouteSettings) && (
            <Route path={toRelativePath(RouteId.settings)}>
              {hasPermission(Permission.RuleList) && (
                <Route index element={<Navigate replace to={RouteIdPart.rule} />} />
              )}
              <Route path={RouteIdPart.rule}>
                <Route
                  index
                  element={
                    <RestrictedRoute needs={{ permission: Permission.RuleEdit }}>
                      <RuleListSourcing />
                    </RestrictedRoute>
                  }
                />
                <Route
                  path={RouteIdPart.create}
                  element={
                    <RestrictedRoute needs={{ permission: Permission.RuleEdit }}>
                      <RuleDetailCreate />
                    </RestrictedRoute>
                  }
                />
                <Route path=":ruleId">
                  <Route
                    index
                    element={
                      <RestrictedRoute needs={{ permission: Permission.RuleEdit }}>
                        <RuleDetail />
                      </RestrictedRoute>
                    }
                  />
                  <Route
                    path={RouteIdPart.log}
                    element={
                      <RestrictedRoute needs={{ permission: Permission.RuleEdit }}>
                        <RuleEvaluationLog />
                      </RestrictedRoute>
                    }
                  >
                    <Route path=":logId" element={<RuleEvaluationLogDetail />} />
                  </Route>
                </Route>
              </Route>
              <Route path={RouteIdPart.smartLinks}>
                <Route path=":id" element={<SmartLinks domain={AstroSmartLinkDomain.SETTINGS} />} />
              </Route>
            </Route>
          )}

          <Route
            path={toRelativePath(RouteId.teamManagement)}
            element={
              <RestrictedRoute needs={{ permission: Permission.TeamManage }}>
                <TeamManagement />
              </RestrictedRoute>
            }
          />
          <Route path={toRelativePath(RouteId.support)} element={<Support />} />

          {features.intake && (
            <Route path={toRelativePath(RouteId.intake)}>
              <Route index element={<IntakeList />} />
              <Route path={RouteIdPart.create} element={<IntakeCreate />} />
              <Route path={RouteIdPart.createChat} element={<IntakeCreateChat />} />

              <Route path={RouteIdPart.chat}>
                <Route
                  path=":id"
                  element={
                    // Chat needs permission on any of the current agents
                    <RestrictedRoute
                      needs={{
                        permission: {
                          any: [
                            Permission.IntakeAgentProcurementKnowledge,
                            Permission.IntakeAgentCatalogueSearch,
                            Permission.IntakeAgentSupplierIdentification,
                            Permission.IntakeAgentSupplierSearch,
                            Permission.IntakeAgentContractComparison,
                            Permission.IntakeAgentQuoteComparison,
                            Permission.IntakeAgentFrontdesk,
                          ],
                        },
                      }}
                    >
                      <IntakeChat />
                    </RestrictedRoute>
                  }
                />
              </Route>
              <Route path=":id" element={<IntakeDetailPage />} />
            </Route>
          )}
          {features.riskSustainability && (
            <Route path={toRelativePath(RouteId.riskSustainability)}>
              <Route index element={<RiskCockpit />} />
              <Route path="risk/:id">
                <Route index element={<RiskDetail />} />
                <Route path=":tabId" element={<RiskDetail />} />
              </Route>
              <Route path="country-report/:id">
                <Route index element={<CountryReportDetail />} />
                <Route path=":tabId" element={<CountryReportDetail />} />
              </Route>
              <Route path={RouteIdPart.smartLinks}>
                <Route path=":id" element={<SmartLinks domain={AstroSmartLinkDomain.RISK} />} />
              </Route>
            </Route>
          )}
          <Route path={toRelativePath(RouteId.demo)} element={<DemoLayout />} />
          {hasPermission({
            any: [
              Permission.BaseGeneral,
              Permission.BaseOrderList,
              Permission.BaseCommodityList,
              Permission.BaseContractList,
              Permission.BaseRequisitionList,
              Permission.BaseSupplierList,
              Permission.AnalyticsView,
            ],
          }) && (
            <Route path={RouteIdPart.analytics}>
              <Route index element={<Analytics />} />

              {hasPermission(Permission.BaseGeneral) && (
                <Route path={kebabCase(BaseDomainType.GENERAL)}>
                  <Route index element={<BaseGeneral />} />
                  <Route path=":tabId" element={<BaseGeneral />} />
                </Route>
              )}
              <Route path={RouteIdPart.base}>
                {hasPermission(Permission.BaseContractList) && (
                  <Route path={kebabCase(BaseDomainType.CONTRACT)}>
                    <Route index element={<ContractList />} />
                    <Route path=":contractId">
                      <Route index element={<ContractDetail />} />
                      <Route path=":tabId" element={<ContractDetail />} />
                    </Route>
                  </Route>
                )}
                {hasPermission(Permission.BaseSupplierList) && (
                  <>
                    <Route path={kebabCase(BaseDomainType.SUPPLIER)}>
                      <Route index element={<BaseSupplierList />} />
                      <Route path="map" element={<BaseSupplierList tab={SupplierListTab.MAP} />} />
                      <Route path=":supplierId">
                        <Route index element={<SupplierDetail />} />
                        <Route path=":tabId" element={<SupplierDetail />} />
                      </Route>
                    </Route>
                    <Route path={kebabCase(BaseDomainType.COMPANY)}>
                      <Route path=":companyId">
                        <Route index element={<AnalyticsCompanyDetail />} />
                        <Route path=":tabId" element={<AnalyticsCompanyDetail />} />
                      </Route>
                    </Route>
                  </>
                )}
                {hasPermission(Permission.BaseOrderList) && (
                  <Route path={kebabCase(BaseDomainType.ORDER)}>
                    <Route index element={<PurchaseOrderList />} />
                    <Route path=":orderId">
                      <Route index element={<AnalyticsPurchaseOrderDetail />} />
                      <Route path=":tabId" element={<AnalyticsPurchaseOrderDetail />} />
                    </Route>
                  </Route>
                )}
                {hasPermission(Permission.BaseCommodityList) && (
                  <Route path={kebabCase(BaseDomainType.COMMODITY)}>
                    <Route index element={<CommodityList />} />
                    <Route path=":commodityId">
                      <Route index element={<CommodityDetail />} />
                      <Route path=":tabId" element={<CommodityDetail />} />
                    </Route>
                  </Route>
                )}
                {hasPermission(Permission.BaseRequisitionList) && (
                  <Route path={kebabCase(BaseDomainType.PURCHASE_REQUISITION)}>
                    <Route index element={<BasePurchaseRequisitionList />} />
                    <Route path=":purchaseRequisitionId">
                      <Route index element={<PurchaseRequisitionDetail />} />
                      <Route path=":tabId" element={<PurchaseRequisitionDetail />} />
                    </Route>
                  </Route>
                )}
                {hasPermission(Permission.BaseCommodityGroupList) && (
                  <Route path={kebabCase(BaseDomainType.COMMODITY_GROUP)}>
                    <Route index element={<CommodityGroupList />} />
                    <Route path=":commodityGroupId">
                      <Route index element={<CommodityGroupDetail />} />
                      <Route path=":tabId" element={<CommodityGroupDetail />} />
                    </Route>
                  </Route>
                )}
                {/* Product Decision to omit Sourcing Project List Page initially - details available through search only */}
                {hasPermission(Permission.SearchIndexSourcingView) && (
                  <Route path={`${kebabCase(BaseDomainType.SOURCING_PROJECT)}/:sourcingProjectId`}>
                    <Route index element={<SourcingProjectDetail />} />
                  </Route>
                )}
              </Route>

              {hasPermission(Permission.AnalyticsView) && (
                <Route path={RouteIdPart.superset}>
                  <Route index element={<Superset />} />
                </Route>
              )}

              <Route path={RouteIdPart.smartLinks}>
                <Route path=":id" element={<SmartLinks domain={AstroSmartLinkDomain.ANALYTICS} />} />
              </Route>
            </Route>
          )}
          {/** Demand */}
          {hasPermission({
            any: [Permission.DemandList],
          }) && (
            <Route path={toRelativePath(RouteId.demand)}>
              <Route index element={<NegotiationDemandList />} />
              {/**CONTRACT DEMAND TODO: Add permissions */}
              <Route path={RouteIdPart.contractDemand}>
                <Route index element={<ContractDemandList />} />
                <Route path={RouteIdPart.coverageAssistant} element={<CoverageAssistant />} />
                <Route path={`${RouteIdPart.sourcingCase}/:id`}>
                  <Route index element={<SourcingCaseDetail />} />
                  <Route path=":tabId" element={<SourcingCaseDetail />} />
                </Route>
                <Route path=":demandId">
                  <Route index element={<ContractDemandDetail />} />
                  <Route path=":tabId" element={<ContractDemandDetail />} />
                </Route>
              </Route>
              {hasPermission(Permission.CaseManualCreate) && (
                <Route path={RouteIdPart.create} element={<NegotiationCaseCreate />} />
              )}
              {hasPermission(Permission.CaseList) && (
                <Route path={NegotiationDomainType.NEGOTIATION_CASE}>
                  <Route index element={<NegotiationCaseList />} />
                  <Route path=":negotiationId">
                    <Route index element={<NegotiationCaseDetail />} />
                    <Route path=":tabId" element={<NegotiationCaseDetail />} />
                  </Route>
                </Route>
              )}
              <Route path=":demandId">
                <Route index element={<NegotiationDemandDetail />} />
                <Route path=":tabId" element={<NegotiationDemandDetail />} />
              </Route>
              <Route path={RouteIdPart.smartLinks}>
                <Route path=":id" element={<SmartLinks domain={AstroSmartLinkDomain.ANALYTICS} />} />
              </Route>
            </Route>
          )}

          {hasPermission(Permission.RouteInternal) && (
            <Route path={toRelativePath(RouteId.internalToolsAll)}>
              <Route index element={<InternalTools />} />
              <Route path={RouteIdPart.smartLinks}>
                <Route
                  path=":id"
                  element={
                    <>
                      <SmartLinks domain={AstroSmartLinkDomain.TOOLS_1} />
                      <SmartLinks domain={AstroSmartLinkDomain.TOOLS_2} />
                      <SmartLinks domain={AstroSmartLinkDomain.TOOLS_3} />
                    </>
                  }
                />
              </Route>
            </Route>
          )}

          {hasPermission(Permission.RouteFoundation) && (
            <Route path={toRelativePath(RouteId.dataFoundationAll)}>
              <Route index element={<DataFoundation />} />
              {hasPermission(Permission.CmdiSavingEdit) && (
                <Route path={`${RouteIdPart.savings}/:id?`} element={<Savings />} />
              )}
              {hasPermission({
                any: [
                  Permission.CmdiAcountingAreaEdit,
                  Permission.CmdiCapabilityEdit,
                  Permission.CmdiCommodityEdit,
                  Permission.CmdiCommodityGroupEdit,
                  Permission.CmdiContactEdit,
                  Permission.CmdiOrganisationEdit,
                  Permission.CmdiSupplierEdit,
                  Permission.CmdiUnitEdit,
                ],
              }) && (
                <Route path=":entityId">
                  <Route index element={<Cmdi />} />
                </Route>
              )}
            </Route>
          )}

          {hasPermission(Permission.KnowledgeList) && (
            <Route path={toRelativePath(RouteId.knowledgeRepository)}>
              <Route index element={<ProcurementKnowledgeList />} />
            </Route>
          )}

          {/** Other */}
          <Route path={toRelativePath(RouteId.home)} element={<Home />} />
          <Route path="*" element={<NotFound />} />

          {/* END PROTECTED ROUTES */}
        </Route>
      </RouterRoutes>
    </>
  );
};

// Helper function to convert absolute paths to relative paths for "*" route route matching
function toRelativePath(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}
